import React from 'react'
import clsx from 'classnames'
import type { CashbackLevel } from 'api/api.types'
import { APIService } from 'api/APIService'
import { useFetch } from 'src/hooks/useFetch'
import { Loader } from 'components/Utility/Loader/Loader'
import styles from './Cashback.module.scss'

// The old markup sets size attributes on <i>; kept so the HTML stays the same
const ICON_SIZE = { width: 16, height: 16 } as React.HTMLAttributes<HTMLElement>

const LEVEL_NAMES = ['BASIC', 'SILVER', 'GOLD']

const fetchCashback = () => APIService.profile.fetchCashback()

/** How far the client is towards a level by spending, 0–100 */
const percentOf = (rule: CashbackLevel['services']) => Math.min(Math.round(rule.actualValue * 100 / rule.desiredValue), 100)

const LevelCircle: React.FC<{ level: CashbackLevel, active: boolean }> = ({ level, active }) => (
  <div className={clsx([styles.circle, active && styles.active])}>
    <span>
      {level.cashbackAmount}
    </span>
    <span className={styles.progressPercent}>
      %
    </span>
  </div>
)

const LevelTitle: React.FC<{ index: number, level: CashbackLevel, active: boolean, withIcon: boolean }> = (props) => {
  const { index, level, active, withIcon } = props
  return (
    <div className={styles.levelTitle}>
      <div className={clsx([styles.circle, styles.checkmark, active && styles.active])}>
        {withIcon && <i {...ICON_SIZE} />}
      </div>
      <div>
        <span className={styles.levelName}>
          {LEVEL_NAMES[index]}
        </span>
        <span className={styles.levelPercent}>
          ({level.cashbackAmount}%)
        </span>
      </div>
    </div>
  )
}

const LevelConditions: React.FC<{ level: CashbackLevel }> = ({ level }) => (
  <div className={styles.levelDescr}>
    <div>
      <strong>Кешбэк от суммы чека {level.cashbackAmount}%.<br /></strong>
      Переход на данный уровень происходит автоматически
      при сумме расходов в
      течение <strong>{level.services.daysRestriction} дней</strong>:<br />
      • {level.services.desiredValue}₽ на услуги<br />
      • {level.goods.desiredValue}₽ на товары
    </div>
  </div>
)

/** The «Мой кешбэк» tab: balance, current level and progress to the next ones */
export const Cashback: React.FC = () => {
  const { response, isLoading } = useFetch(fetchCashback)

  if (isLoading || !response.result) {
    return <div className={styles.root}><Loader /></div>
  }
  const { progress, amount } = response.result
  const currentLevel = progress.currentLevel ?? 1
  const [basic, silver, gold] = progress.levels

  const silverServices = percentOf(silver.services)
  const goldServices = currentLevel < 2 ? 0 : percentOf(gold.services)
  const servicesBackgroundWidth = (silverServices + (silverServices === 100 ? goldServices : 0)) / 2

  return (
    <div className={styles.root}>
      <div className={styles.cashbackAmountContainer}>
        <div className={styles.stars}>
          <div className={clsx([styles.cashbackAmount, styles.circleBorderContainer])}>
            <div className={clsx([styles.cashbackAmountContent, styles.circleBorderContent])}>
              <div>Кешбэк</div>
              <div className={styles.amountNumber}>{amount}</div>
              <div>рублей</div>
            </div>
          </div>
        </div>
        <div className={styles.currentLevelContainer}>
          <div className={styles.currentLevelTitle}>
            {LEVEL_NAMES[currentLevel - 1]}
          </div>
          <div className={styles.currentLevelValue}>
            Кешбэк {progress.levels[currentLevel - 1].cashbackAmount}%.
          </div>
        </div>
      </div>
      <div className={styles.progressContainer}>
        <div className={styles.progressPart}>
          <div className={styles.progressPartTitle}>
            Расходы
          </div>
          <div className={styles.progressLine}>
            <div className={styles.progressBackground} style={{ width: `${servicesBackgroundWidth}%` }} />
            <LevelCircle level={basic} active={currentLevel === 1} />
            <LevelCircle level={silver} active={currentLevel === 2} />
            <LevelCircle level={gold} active={currentLevel === 3} />
            <div className={styles.decorationBorder} />
          </div>
          <div>
            {[silver, gold].map((level, index) => (
              <div key={index} className={styles.progressInfo}>
                За последние {level.checks.daysRestriction} дней
                потрачено {level.checks.actualValue}₽{' '}/{' '}{level.checks.desiredValue}₽
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.levelsDescr}>
        <div className={styles.level}>
          <LevelTitle index={0} level={basic} active={currentLevel === 1} withIcon />
          <div className={styles.levelDescr}>
            Базовый уровень. Начисление происходит с первого визита.
          </div>
        </div>
        <div className={styles.level}>
          <LevelTitle index={1} level={silver} active={currentLevel === 2} withIcon />
          <LevelConditions level={silver} />
        </div>
        <div className={styles.level}>
          <LevelTitle index={2} level={gold} active={currentLevel === 3} withIcon={false} />
          <LevelConditions level={gold} />
        </div>
      </div>
    </div>
  )
}
